/**
 * Google Analytics Query Command
 * 
 * Allows AI to query Google Analytics data using natural language.
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { CommandExecutionContext } from '../../types/commands';
import { StandardToolResult } from '../../types/standardToolResults';
import fetchService from '../../services/fetchService';
import { API_BASE_URL } from '../../api/config';

export const google_analytics_query: SimpleDocCommand = {
  id: 'google_analytics_query',
  logicalName: 'google_analytics_query',
  version: 'v2',
  description: `Query Google Analytics data using natural language.

**QUERY TYPES:**
- "users by country last 30 days"
- "top 10 pages by pageviews this month"
- "sessions by device yesterday"
- "revenue by source last week"

**METRICS:**
users, sessions, pageviews, bounce rate, engagement rate, conversions, revenue, events

**DIMENSIONS:**
date, country, city, device, browser, page, source, medium, campaign

**DATE RANGES:**
"today", "yesterday", "last N days/weeks/months", "this week/month", "last week/month" (default: last 7 days)

**EXAMPLE:**

{ "query": "show me users by country last 30 days" }
`,

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Natural language query describing what data to retrieve from Google Analytics',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Query cannot be empty';
          }
          if (value.length > 500) {
            return 'Query too long (max 500 characters)';
          }
          return null;
        },
        examples: [
          'show me users by country last 30 days',
          'top 10 pages by pageviews this month',
          'sessions by device yesterday'
        ]
      },
      {
        name: 'property_id',
        type: 'string',
        description: 'Optional: Specific GA4 property ID to query (overrides default property)',
        required: false,
        validator: (value: string) => {
          if (value && !/^\d+$/.test(value)) {
            return 'Property ID must be numeric';
          }
          return null;
        }
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { query, property_id } = params;
    
    console.log('[google_analytics_query] Executing query:', query);
    
    try {
      // Call backend API
      // fetchService already returns parsed JSON data, not a Response object
      const data = await fetchService(`${API_BASE_URL}/google-analytics/query`, {
        method: 'POST',
        body: JSON.stringify({
        query,
        property_id
        })
      });
      
      // Check for errors in the data returned by fetchService
      if (data.error || data.detail) {
        // Handle specific error cases
        if (data.status === 400 && data.detail?.includes('not connected')) {
          return {
            status: 'error',
            message: 'Google Analytics is not connected. Please connect it in Settings → Integrations.',
            error: {
              code: 'not_connected',
              category: 'validation' as const,
              details: data.detail
            },
            format: 'plaintext'
          };
        }
        
        if (data.status === 400 && (data.detail?.includes('No default property') || data.detail?.includes('no default property'))) {
          return {
            status: 'error',
            message: 'No default Google Analytics property set. Please set one in Settings → Integrations.',
            error: {
              code: 'no_default_property',
              category: 'validation' as const,
              details: data.detail
            },
            format: 'plaintext'
          };
        }
        
        return {
          status: 'error',
          message: 'Google Analytics query failed',
          error: {
            code: 'query_failed',
            category: 'execution' as const,
            details: data.detail || 'Unknown error occurred'
          },
          format: 'plaintext'
        };
      }
      
      const result = data;
      
      console.log('[google_analytics_query] Query successful:', {
        row_count: result.row_count,
        metrics: result.parsed_params?.metrics,
        dimensions: result.parsed_params?.dimensions
      });
      
      // Critical: Validate metadata structure before accessing
      if (!result.metadata || !result.metadata.date_range || !result.metadata.metrics || !result.metadata.dimensions) {
        console.error('[google_analytics_query] Invalid result structure:', result);
        return {
          status: 'error',
          message: 'Invalid response from Google Analytics',
          error: {
            code: 'invalid_response',
            category: 'execution' as const,
            details: 'Backend returned incomplete metadata'
          },
          format: 'plaintext'
        };
      }
      
      // Format results
      const dateRange = result.metadata.date_range;
      const metrics = result.metadata.metrics.join(', ');
      const dimensions = result.metadata.dimensions.join(', ');
      
      return {
        status: 'success',
        message: `Retrieved ${result.row_count} rows from Google Analytics.

**Query Details:**
- Date Range: ${dateRange.start} to ${dateRange.end}
- Metrics: ${metrics}
- Dimensions: ${dimensions}

**Results:**
${result.row_count > 0 ? 'Data is available in the response.' : 'No data found for this query.'}`,
        data: {
          rows: result.rows,
          row_count: result.row_count,
          metadata: result.metadata,
          parsed_params: result.parsed_params,
          summary: {
            total_rows: result.row_count,
            date_range: `${dateRange.start} to ${dateRange.end}`,
            metrics: result.metadata.metrics,
            dimensions: result.metadata.dimensions
          }
        },
        format: 'json'
      };
      
    } catch (error: any) {
      console.error('[google_analytics_query] Error:', error);
      
      // Extract detailed error information
      let errorMessage = 'Failed to execute Google Analytics query';
      let errorDetails = error.message;
      
      if (error.data?.detail) {
        errorDetails = error.data.detail;
        errorMessage = errorDetails;
      }
      
      // Categorize by HTTP status
      let errorCategory = 'execution';
      if (error.status === 400) errorCategory = 'validation';
      if (error.status === 403) errorCategory = 'permission';
      if (error.status === 404) errorCategory = 'validation';
      
      return {
        status: 'error',
        message: errorMessage,
        error: {
          code: error.status ? `http_${error.status}` : 'execution_error',
          category: errorCategory as 'execution' | 'validation' | 'permission',
          details: errorDetails
        },
        format: 'plaintext'
      };
    }
  }
};
